/**
 * Renders one step: content blocks, input fields, and the navigation.
 * Presentational — answer state lives in `FunnelApp`.
 */
import type { Answers, Step } from "../types";
import type { FieldValue } from "../logic/validation";
import { Blocks } from "./Blocks";
import { FieldView } from "./Fields";

export interface StepRendererProps {
  step: Step;
  answers: Answers;
  errors: Record<string, string>;
  continueLabel: string;
  showBack: boolean;
  onFieldChange: (fieldId: string, value: FieldValue) => void;
  onFieldBlur?: (fieldId: string) => void;
  onContinue: () => void;
  onBack: () => void;
}

export function StepRenderer({
  step,
  answers,
  errors,
  continueLabel,
  showBack,
  onFieldChange,
  onFieldBlur,
  onContinue,
  onBack,
}: StepRendererProps) {
  const visibleFields = step.fields.filter((f) => f.type !== "hidden");

  // Steps containing a consent_checkbox field ARE the TCPA submission point.
  // TrustedForm Tagged Consent (per ActiveProspect docs 2026-05-24) requires:
  //   - an `offer` element wrapping the entire consent area
  //   - exactly ONE `submit` element per offer
  //   - the inner consent_checkbox field emits consent-opt-in + consent-language
  // Earlier-step Continue buttons advance the funnel but are NOT submissions —
  // they get neither the offer wrapper nor the submit attribute.
  const hasConsent = step.fields.some((f) => f.type === "consent_checkbox");
  const containerProps = hasConsent
    ? { class: "fe-fade", "data-tf-element-role": "offer", style: "display:flex;flex-direction:column;gap:14px" }
    : { class: "fe-fade", style: "display:flex;flex-direction:column;gap:14px" };

  return (
    <div {...containerProps}>
      <Blocks blocks={step.blocks} />

      {visibleFields.map((field) => (
        <FieldView
          key={field.id}
          field={field}
          value={answers[field.id] as FieldValue}
          error={errors[field.id]}
          onChange={(v) => onFieldChange(field.id, v)}
          onBlur={onFieldBlur ? () => onFieldBlur(field.id) : undefined}
        />
      ))}

      <div class="fe-nav">
        <button
          type="button"
          class="fe-btn"
          onClick={onContinue}
          {...(hasConsent ? { "data-tf-element-role": "submit" } : {})}
        >
          {continueLabel}
        </button>
        {showBack ? (
          <button type="button" class="fe-btn fe-btn--ghost" onClick={onBack}>
            ← Back
          </button>
        ) : null}
      </div>

      {step.footer_blocks && step.footer_blocks.length > 0 ? (
        <Blocks blocks={step.footer_blocks} />
      ) : null}
    </div>
  );
}
